import { useState } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Loader2, Calendar, MapPin, CreditCard, AlertCircle, Target, Plus, Trash2, Users, Camera } from 'lucide-react';
import { toast } from 'sonner';

const EditEventModal = ({ event, onClose, onSuccess }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [formData, setFormData] = useState({
    title: event.title,
    description: event.description,
    blessings: event.blessings || '',
    invited_by: event.invited_by || '',
    date: event.date,
    time: event.time || '12:00',
    location: event.location,
    expected_guests: event.expected_guests || '',
    is_fundraising_paused: event.is_fundraising_paused || false,
    upi_ids: event.upi_ids || [],
    event_photos: event.event_photos || []
  });

  const [contributionGoals, setContributionGoals] = useState(event.contribution_goals || []);
  const [newUpiId, setNewUpiId] = useState('');

  const handleAddUPI = () => {
    if (!newUpiId.trim()) {
      toast.error('Please enter a UPI ID');
      return;
    }

    if (formData.upi_ids.includes(newUpiId.trim())) {
      toast.error('This UPI ID is already added');
      return;
    }

    setFormData({
      ...formData,
      upi_ids: [...formData.upi_ids, newUpiId.trim()]
    });
    setNewUpiId('');
    toast.success('UPI ID added');
  };

  const handleRemoveUPI = (upiId) => {
    setFormData({
      ...formData,
      upi_ids: formData.upi_ids.filter(id => id !== upiId)
    });
  };

  const handleGoalChange = (index, field, value) => {
    const updated = [...contributionGoals];
    updated[index][field] = value;
    setContributionGoals(updated);
  };

  const handleAddGoal = () => {
    setContributionGoals([
      ...contributionGoals,
      {
        id: `goal_${Date.now()}`,
        title: '',
        description: '',
        target_amount: 0,
        current_amount: 0,
        is_completed: false,
        is_paused: false,
        additional_info: '',
        minimum_contribution: null
      }
    ]);
  };

  const handleRemoveGoal = (index) => {
    if (contributionGoals.length > 1) {
      setContributionGoals(contributionGoals.filter((_, i) => i !== index));
    } else {
      toast.error('Event must have at least one contribution goal');
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (formData.event_photos.length + files.length > 4) {
      toast.error('Maximum 4 photos allowed');
      return;
    }

    setUploadingPhoto(true);
    
    for (const file of files) {
      if (formData.event_photos.length >= 4) break;
      
      try {
        const formDataToUpload = new FormData();
        formDataToUpload.append('file', file);

        const response = await axios.post(`${API}/upload-photo`, formDataToUpload, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        setFormData(prev => ({
          ...prev,
          event_photos: [...prev.event_photos, response.data.photo_url]
        }));
      } catch (error) {
        toast.error('Failed to upload photo');
        console.error(error);
      }
    }
    
    setUploadingPhoto(false);
    e.target.value = ''; // Reset input
  };

  const handleRemovePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      event_photos: prev.event_photos.filter((_, i) => i !== index)
    }));
  };

  const handleUpdate = async () => {
    if (!formData.title || !formData.date || !formData.location) {
      toast.error('Please fill all required fields');
      return;
    }

    // Validate goals
    const validGoals = contributionGoals.filter(goal => goal.title && goal.target_amount > 0);
    if (validGoals.length === 0) {
      toast.error('Please have at least one valid contribution goal');
      return;
    }

    // Validate date is within 60 days
    const selectedDate = new Date(formData.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + 60);

    if (selectedDate < today) {
      toast.error('Event date cannot be in the past');
      return;
    }

    if (selectedDate > maxDate) {
      toast.error('Event date must be within the next 60 days');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        contribution_goals: validGoals.map(goal => ({
          ...goal,
          target_amount: parseFloat(goal.target_amount)
        }))
      };

      const response = await axios.put(
        `${API}/events/${event.id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data) {
        toast.success('Event updated successfully!');
        onSuccess?.();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update event');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-event-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Edit Event</DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-title">Event Title *</Label>
              <Input
                id="edit-title"
                placeholder="Event title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                data-testid="edit-title-input"
              />
            </div>

            <div>
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                placeholder="Event description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                data-testid="edit-description-input"
              />
            </div>

            <div>
              <Label htmlFor="edit-blessings">Blessings / Additional Message</Label>
              <Textarea
                id="edit-blessings"
                placeholder="Add a special message or blessings for your guests..."
                value={formData.blessings}
                onChange={(e) => setFormData({ ...formData, blessings: e.target.value })}
                rows={3}
                data-testid="edit-blessings-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Share a heartfelt message or blessing with your guests (optional)
              </p>
            </div>

            <div>
              <Label htmlFor="edit-invited-by">Invited By (Family Name)</Label>
              <Input
                id="edit-invited-by"
                placeholder="e.g., The Sharma Family"
                value={formData.invited_by}
                onChange={(e) => setFormData({ ...formData, invited_by: e.target.value })}
                data-testid="edit-invited-by-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                This will appear in the invitation message (optional)
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="edit-date">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Event Date *
                </Label>
                <Input
                  id="edit-date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  max={new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                  data-testid="edit-date-input"
                />
              </div>

              <div>
                <Label htmlFor="edit-time">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Event Time *
                </Label>
                <Input
                  id="edit-time"
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  data-testid="edit-time-input"
                />
              </div>

              <div>
                <Label htmlFor="edit-location">
                  <MapPin className="inline h-4 w-4 mr-1" />
                  Location *
                </Label>
                <Input
                  id="edit-location"
                  placeholder="Event location"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  data-testid="edit-location-input"
                />
              </div>
            </div>

            <div className="mt-4">
              <Label htmlFor="edit-expected-guests">
                <Users className="inline h-4 w-4 mr-1" />
                Expected Number of Guests
              </Label>
              <Input
                id="edit-expected-guests"
                type="number"
                placeholder="e.g., 50"
                value={formData.expected_guests}
                onChange={(e) => setFormData({ ...formData, expected_guests: e.target.value })}
                min="1"
                data-testid="edit-expected-guests-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                How many guests are you planning to invite? (optional)
              </p>
            </div>
          </div>

          {/* Contribution Goals */}
          <div className="border-2 border-purple-100 rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <Label className="text-base font-semibold">
                <Target className="inline h-5 w-5 mr-1" />
                Edit Contribution Goals
              </Label>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddGoal}
                data-testid="add-goal-edit-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Goal
              </Button>
            </div>

            <div className="space-y-3 max-h-80 overflow-y-auto">
              {contributionGoals.map((goal, index) => (
                <div
                  key={goal.id || index}
                  className="p-3 border-2 border-gray-200 rounded-lg space-y-2 bg-white"
                  data-testid={`edit-goal-${index}`}
                >
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-gray-700">Goal {index + 1}</h4>
                    {contributionGoals.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveGoal(index)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`remove-goal-edit-${index}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <div>
                    <Label className="text-sm">Goal Title *</Label>
                    <Input
                      placeholder="e.g., Venue Rental"
                      value={goal.title}
                      onChange={(e) => handleGoalChange(index, 'title', e.target.value)}
                      data-testid={`edit-goal-${index}-title`}
                    />
                  </div>

                  <div>
                    <Label className="text-sm">Description</Label>
                    <Textarea
                      placeholder="Details about this goal..."
                      value={goal.description}
                      onChange={(e) => handleGoalChange(index, 'description', e.target.value)}
                      rows={2}
                      data-testid={`edit-goal-${index}-description`}
                    />
                  </div>

                  <div>
                    <Label className="text-sm">Additional Info / Shopping URL</Label>
                    <Input
                      placeholder="e.g., https://amazon.in/product/xyz"
                      value={goal.additional_info || ''}
                      onChange={(e) => handleGoalChange(index, 'additional_info', e.target.value)}
                      data-testid={`edit-goal-${index}-info`}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Add a shopping link or helpful information for contributors
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-sm">Target Amount (₹) *</Label>
                      <Input
                        type="number"
                        placeholder="5000"
                        value={goal.target_amount}
                        onChange={(e) => handleGoalChange(index, 'target_amount', e.target.value)}
                        min="0"
                        data-testid={`edit-goal-${index}-amount`}
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Min Contribution (₹)</Label>
                      <Input
                        type="number"
                        placeholder="e.g., 100"
                        value={goal.minimum_contribution || ''}
                        onChange={(e) => handleGoalChange(index, 'minimum_contribution', e.target.value)}
                        min="0"
                        data-testid={`edit-goal-${index}-min`}
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Current Raised (₹)</Label>
                      <Input
                        type="number"
                        value={goal.current_amount || 0}
                        disabled
                        className="bg-gray-100"
                      />
                    </div>
                  </div>

                  {/* Goal-Level Pause/Active Toggle */}
                  <div className="flex items-center justify-between mt-3 p-2 bg-gray-50 rounded-md border border-gray-200">
                    <div>
                      <Label className="text-sm font-semibold text-gray-700">
                        Goal Status
                      </Label>
                      <p className="text-xs text-gray-500">
                        {goal.is_paused ? 'Contributions paused for this goal' : 'Accepting contributions'}
                      </p>
                    </div>
                    <Switch
                      checked={!goal.is_paused}
                      onCheckedChange={(checked) => handleGoalChange(index, 'is_paused', !checked)}
                      data-testid={`edit-goal-${index}-pause-toggle`}
                    />
                  </div>

                  {goal.current_amount > 0 && (
                    <p className="text-xs text-blue-600">
                      Note: ₹{goal.current_amount.toLocaleString()} already raised for this goal
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Fundraising Control */}
          <div className="border-2 border-yellow-200 rounded-lg p-4 bg-yellow-50">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Label className="text-base font-semibold text-gray-900">
                  Fundraising Status
                </Label>
                <p className="text-sm text-gray-600 mt-1">
                  {formData.is_fundraising_paused 
                    ? 'Contributions are currently paused. Guests will see "Funds are not accepting at the moment"'
                    : 'Contributions are active. Guests can contribute to goals.'
                  }
                </p>
              </div>
              <Switch
                checked={!formData.is_fundraising_paused}
                onCheckedChange={(checked) => setFormData({ ...formData, is_fundraising_paused: !checked })}
                data-testid="fundraising-toggle"
              />
            </div>
          </div>

          {/* UPI IDs */}
          <div className="border-2 border-purple-100 rounded-lg p-4 space-y-3">
            <Label className="text-base font-semibold">
              <CreditCard className="inline h-5 w-5 mr-1" />
              UPI Payment IDs
            </Label>
            
            {/* Add UPI */}
            <div className="flex gap-2">
              <Input
                placeholder="Enter UPI ID (e.g., user@paytm)"
                value={newUpiId}
                onChange={(e) => setNewUpiId(e.target.value)}
                data-testid="new-upi-input"
              />
              <Button
                variant="outline"
                onClick={handleAddUPI}
                data-testid="add-upi-btn"
              >
                Add
              </Button>
            </div>

            {/* UPI List */}
            {formData.upi_ids.length > 0 ? (
              <div className="space-y-2">
                {formData.upi_ids.map((upiId, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center p-3 bg-contribo-navy bg-opacity-50 rounded-lg"
                    data-testid={`upi-${index}`}
                  >
                    <span className="font-medium text-gray-900">{upiId}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveUPI(upiId)}
                      className="text-red-500 hover:text-red-700"
                      data-testid={`remove-upi-${index}`}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">No UPI IDs added yet</p>
                <p className="text-xs text-gray-500 mt-1">Add UPI IDs to accept payments</p>
              </div>
            )}
          </div>

          {/* Event Photos */}
          <div className="border-2 border-purple-100 rounded-lg p-4 space-y-3">
            <Label className="text-base font-semibold">
              <Camera className="inline h-5 w-5 mr-1" />
              Event Photos (Optional)
            </Label>
            <p className="text-sm text-gray-600">Upload up to 4 photos for your event</p>
            
            {/* Photo Upload Button */}
            <div>
              <Label htmlFor="edit-photo-upload" className="cursor-pointer">
                <div className="border-2 border-dashed border-contribo-gold rounded-lg p-4 text-center hover:bg-contribo-gold hover:bg-opacity-5 transition-colors">
                  <input
                    id="edit-photo-upload"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                    disabled={formData.event_photos.length >= 4 || uploadingPhoto}
                  />
                  {uploadingPhoto ? (
                    <div className="flex items-center justify-center">
                      <Loader2 className="h-6 w-6 animate-spin text-contribo-gold mr-2" />
                      <span className="text-contribo-navy">Uploading...</span>
                    </div>
                  ) : (
                    <>
                      <Plus className="h-6 w-6 mx-auto mb-2 text-contribo-gold" />
                      <p className="text-contribo-navy font-semibold text-sm">
                        Click to upload photos
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formData.event_photos.length}/4 photos uploaded
                      </p>
                    </>
                  )}
                </div>
              </Label>
            </div>

            {/* Photo Preview Grid */}
            {formData.event_photos.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {formData.event_photos.map((photo, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={photo}
                      alt={`Event photo ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg border-2 border-contribo-navy"
                    />
                    <button
                      onClick={() => handleRemovePhoto(index)}
                      className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      type="button"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={onClose}
              data-testid="cancel-edit-btn"
            >
              Cancel
            </Button>
            <Button
              className="flex-1 bg-gradient-to-r from-contribo-navy to-contribo-gold text-white"
              onClick={handleUpdate}
              disabled={loading}
              data-testid="save-event-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditEventModal;
